import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * M6 — fill rate and cancellation reasons per tier × buy-in.
 *
 * `PAYMENT_TIMEOUT` stays in the breakdown and every reason is emitted even at
 * zero: no writer remains anywhere in `apps/api/src`, but historical rows do,
 * so a breakdown assembled from whatever the window happens to contain drops
 * the reason from every range that covers them.
 */
export const ArenaOpsMatchmakingQueueHealthRowSchema = z.object({
  /**
   * The bucket's identity: `free`, `$1`, `$5`, `$10`, `$20`, `$50`, `S&G $10`.
   * Composite, and the key `oracle.sql` groups on. Key on this, not on `tier`
   * and `buyInUsdt` separately: the oracle folds every zero-fee tier into one
   * `free` bucket, so a `(tier, amount)` key splits rows the oracle keeps
   * together the moment a non-FREE tier carries a zero fee.
   */
  buyInLabel: z.string(),
  /** The label's tier half, split out. `SPIN_AND_GO`, `PAID` or `FREE`. */
  tier: z.string(),
  /** The label's amount half, split out. Decimal-USDT string. */
  buyInUsdt: ArenaOpsUsdtSchema,
  /** Every entry created in the window, terminal or not. */
  entries: z.number().int(),
  matched: z.number().int(),
  cancelled: z.number().int(),
  /**
   * `matched ÷ (matched + cancelled)` — the denominator is TERMINAL entries,
   * NOT `entries`. An entry still sitting in the queue has not failed to fill,
   * and counting it as one makes every recent window look broken. Recomputing
   * this as `matched ÷ entries` gives a different, wrong number.
   *
   * A RATIO in `[0, 1]` at 4 dp, never a percentage. Empty string when nothing
   * in the bucket has reached a terminal state — no rate exists yet.
   */
  fillRate: z.string(),
  /**
   * All five enum reasons plus `NULL_REASON`, always present, in that order.
   * `NULL_REASON` counts entries cancelled with no reason stamped — a live
   * guard against a new writer forgetting one.
   */
  cancellationsByReason: z.array(z.object({ reason: z.string(), count: z.number().int() })),
});

export const ArenaOpsMatchmakingQueueHealthResponseSchema = z.object({
  rows: z.array(ArenaOpsMatchmakingQueueHealthRowSchema),
});
export type ArenaOpsMatchmakingQueueHealthResponse = z.infer<typeof ArenaOpsMatchmakingQueueHealthResponseSchema>;
